import { IcEdit, IcPlus as IcPlus24, IcStatusLosange, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, HStack, Icon, IconButton, Separator, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { FleetSelectors } from '@redux/utm/utm.selectors';
import { useDeleteFleetMutation } from '@services/utm/fleet.api';
import { createErrorMessage } from '@utils/common.utils';
import { getClearanceColor } from '@utils/data/notification.utils';
import { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { NormalizedFleet } from '@/types/utm/fleet.type';

import { useUtmContext, UtmItemTypeEnum } from '../UtmManagement';

type FleetsActionsProps = {
  canCreateFleet: boolean;
  fleetsLength: number;
  onCreate: () => void;
};

function FleetsActions({ canCreateFleet, fleetsLength, onCreate }: Readonly<FleetsActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.utm.fleets.result" values={{ count: fleetsLength }} />
      </Text>
      {canCreateFleet && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function FleetsHeader() {
  return (
    <>
      <Text width="220px" color="neutral.500">
        <FormattedMessage id="components.utm.fleets.name" />
      </Text>
      <Text width="240px" color="neutral.500">
        <FormattedMessage id="components.utm.fleets.strategicClearance" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.utm.fleets.nbDrones" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.utm.fleets.nbPilots" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.utm.fleets.nbChips" />
      </Text>
    </>
  );
}

export default function Fleets() {
  const { formatMessage } = useIntl();
  const fleets = useAppSelector(FleetSelectors.selectAllFleets);
  const [selectedFleet, setSelectedFleet] = useState<NormalizedFleet | null>(null);
  const [deleteFleet, { isLoading }] = useDeleteFleetMutation();
  const canEditFleet = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_FLEET));
  const canCreateFleet = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_FLEET));
  const canDeleteFleet = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_FLEET));

  const { setEditedItem } = useUtmContext();

  return (
    <CockpitListLayout
      dialog={
        <CockpitDialog
          open={selectedFleet !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedFleet(null)}
          onConfirm={() => {
            if (!selectedFleet) {
              return;
            }
            deleteFleet(selectedFleet.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage({ id: 'components.utm.fleets.message.delete' }, { name: selectedFleet.name }),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedFleet(null);
              })
              .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
          }}
        >
          <Text color="neutral.100" width="100%" whiteSpace="pre-line">
            <FormattedMessage
              id="components.utm.fleets.popup.descriptionDelete"
              values={{ name: selectedFleet?.name }}
            />
          </Text>
        </CockpitDialog>
      }
      actions={
        <FleetsActions
          canCreateFleet={canCreateFleet}
          fleetsLength={fleets.length}
          onCreate={() => setEditedItem({ type: UtmItemTypeEnum.FLEET, value: null })}
        />
      }
      header={fleets.length > 0 && <FleetsHeader />}
    >
      <For
        each={fleets}
        fallback={
          canCreateFleet ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UtmItemTypeEnum.FLEET, value: null })}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.utm.fleets.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(fleet, index) => (
          <Fragment key={index}>
            {index !== 0 && <Separator size="md" borderColor="neutral.900" />}
            <HStack
              width="100%"
              paddingLeft={5}
              gap={2}
              alignItems="center"
              height="56px"
              flexShrink={0}
              _hover={{ backgroundColor: 'neutral.700' }}
            >
              <Tooltip content={fleet.name} openDelay={500}>
                <Text lineClamp={1} width="220px" fontWeight="medium" color="neutral.200">
                  {fleet.name}
                </Text>
              </Tooltip>
              <HStack gap={1} width="240px">
                <Icon as={IcStatusLosange} boxSize="24px" color={getClearanceColor(fleet.strategicClearance)} />
                <Text width="100%">
                  <FormattedMessage id={`clearance.${fleet.strategicClearance}`} />
                </Text>
              </HStack>
              <Text width="176px">{fleet.drones.length}</Text>
              <Text width="176px">{fleet.pilots.length}</Text>
              <Text width="176px">{fleet.chips.length}</Text>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditFleet && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => setEditedItem({ type: UtmItemTypeEnum.FLEET, value: fleet })}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canDeleteFleet && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedFleet(fleet)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </HStack>
            {index === fleets.length - 1 && <Separator size="md" borderColor="neutral.900" />}
          </Fragment>
        )}
      </For>
    </CockpitListLayout>
  );
}
